import React, { useEffect, useState } from "react";
import { LoadingIcon, EditLargeIcon } from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import FormHeader from "../form/Header";
import Switch from "../form/Switch";
import Popup from "../Popup";
import ConfirmModal from "../ConfirmModal";
import { UpdateSellSpecialtyItem } from "../../api/sell-specialty-items/updateSellSpecialtyItem";
import { CentsToDollars, DollarsToCents } from "../../utils/formatPrice";
import { useTranslation } from "react-i18next";

const EditSellSpecialtyItem = ({ specialtyItem, onUpdate, onClose, ...props }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [confirmingPrice, setConfirmingPrice] = useState(false);

  const [name, setName] = useState(specialtyItem.name || "");
  const [price, setPrice] = useState(CentsToDollars(specialtyItem.unit_price));
  const [taxable, setTaxable] = useState(!!specialtyItem.taxable);
  const [isActive, setIsActive] = useState(!!specialtyItem.is_active);
  const [error, setError] = useState("");

  const priceChanged =
    DollarsToCents(price) !== Number(specialtyItem.unit_price);

  const save = async () => {
    setLoading(true);
    setSubmitDisabled(true);

    try {
      await UpdateSellSpecialtyItem(specialtyItem.id, {
        name,
        unit_price: DollarsToCents(price),
        taxable,
        is_active: isActive,
      });
      setLoading(false);
      setSubmitDisabled(false);
      onUpdate();
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSubmitDisabled(false);
      setConfirmingPrice(false);
    }
  };

  // A price change is the only edit that alters what the next customer is
  // charged, so it is the only one that asks first.
  const handleSubmit = (e) => {
    e.preventDefault();

    if (priceChanged) {
      setConfirmingPrice(true);
      return;
    }

    save();
  };

  useEffect(() => {
    const priceIsValid = price !== "" && Number(price) >= 0;
    const changed =
      name !== (specialtyItem.name || "") ||
      priceChanged ||
      taxable !== !!specialtyItem.taxable ||
      isActive !== !!specialtyItem.is_active;

    setSubmitDisabled(!name || !priceIsValid || !changed);
    // eslint-disable-next-line
  }, [name, price, taxable, isActive, specialtyItem]);

  if (confirmingPrice) {
    return (
      <ConfirmModal
        title={t("Change the price?")}
        question={t(
          "This changes nothing already sold, but everything from the next sale onward."
        )}
        confirmButtonText={t("Save")}
        loadingText={t("Saving...")}
        deleteLoading={loading}
        deleteErrorMessage={error}
        onClose={() => setConfirmingPrice(false)}
        onConfirm={save}
      />
    );
  }

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <form method="post" onSubmit={handleSubmit}>
        <FormHeader
          title={t("Edit Specialty Item")}
          description={t(
            "Specialty items are sold at a fixed price at every office in the company."
          )}
          icon={<EditLargeIcon />}
        ></FormHeader>

        <div className="row mb-5 mt-5 align-items-center">
          <div className="col-xs-12">
            <Switch title={t("Active")}>
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Switch>
            <p>
              {t(
                "Setting an item to Inactive removes it from the agent's dropdown. It stays on every sale already placed and in all reports."
              )}
            </p>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-xs-12">
            <FormSection title={t("Item Name")} required>
              <input
                type="text"
                name="name"
                id="name"
                maxLength="255"
                placeholder={t("Item name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormSection>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-xs-6">
            <FormSection title={t("Price")} required>
              <input
                type="number"
                name="unit_price"
                id="unit_price"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </FormSection>
          </div>
          <div className="col-xs-6">
            <FormSection title={t("Tax Treatment")} required>
              <select
                name="taxable"
                id="taxable"
                value={taxable ? "taxable" : "non_taxable"}
                onChange={(e) => setTaxable(e.target.value === "taxable")}
              >
                <option value="taxable">{t("Taxable")}</option>
                <option value="non_taxable">{t("Non-taxable")}</option>
              </select>
            </FormSection>
          </div>
        </div>

        <div className="text-right">
          <button
            className={`btn btn-primary btn-medium ${
              loading ? "btn-loading" : ""
            }`}
            type="submit"
            name="submit"
            disabled={submitDisabled}
          >
            {loading ? <LoadingIcon /> : ""}
            {t("Save")}
          </button>
        </div>
        {error && <p className="color-error text-center mt-3">{error}</p>}
      </form>
    </Popup>
  );
};

export default EditSellSpecialtyItem;
